import {
  COMPOSITION_GET_DATA_V2,
  type CompositionV2DataQuery,
  type CompositionV2Entity,
  type GetPayV2Query,
  PAY_V2_QUERY,
  type PayV2Entity,
  type PayV2Req,
} from '@revelio/data-access';

import { environment } from '../../../../../environments/environment';
import { useSelectionListState } from '../../../../../shared/selection-lists/hooks/useSelectionListState';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import { getV2Filters } from '../../../filters/utils/getV2Filters';
import { combineTwoQueryStatuses } from '../market-conditions/market-condition-metrics-utils';
import { DataProviderQueryStatus } from '../types';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { getSegmentEntityType } from './getSegmentEntityType';

const isProd = environment.production;

const MAX_SEGMENT_ENTITIES = isProd ? 6 : 10;

export function usePayAndCompositionQueries({
  primaryDimension,
  secondaryDimension,
  entitiesOverride,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: 'geography' | 'role';
  entitiesOverride?: CompositionV2Entity[];
}) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListState();
  const geographyMode = useGeographyMode();

  const compositionVariables = getCompositionV2Filters({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    selectionLists: selectionLists ?? undefined,
    geographyMode,
  });

  const compositionEntities = (() => {
    if (Array.isArray(entitiesOverride) && entitiesOverride.length > 0) {
      return entitiesOverride;
    }
    if (Array.isArray(compositionVariables.entities)) {
      return compositionVariables.entities;
    }
    return [compositionVariables.entities];
  })();

  const breakdownOptionsSingle = (() => {
    const opts = compositionVariables.breakdownOptions;
    if (secondaryDimension === 'geography' && !!opts?.Geography) {
      return { Geography: opts.Geography };
    }
    if (secondaryDimension === 'role' && !!opts?.Role) {
      return { Role: opts.Role };
    }
    return undefined;
  })();

  const compositionVariablesFinal = {
    ...compositionVariables,
    entities: compositionEntities,
    breakdownOptions:
      breakdownOptionsSingle ?? compositionVariables.breakdownOptions,
  };

  const pauseComposition =
    compositionVariablesFinal.entities.length === 0 ||
    !compositionVariablesFinal.filters?.start_date ||
    !compositionVariablesFinal.filters?.end_date;

  const [{ data: compositionData, status: compositionStatus }] =
    useUrqlQueryWithStatus<CompositionV2DataQuery>({
      query: COMPOSITION_GET_DATA_V2,
      variables: compositionVariablesFinal,
      pause: pauseComposition,
    });

  const compositionRes = compositionData?.composition_v2;
  const segmentEntityType = compositionRes
    ? getSegmentEntityType({
        secondaryDimension,
        compositionFilters: compositionVariablesFinal.filters,
      })
    : null;

  const segmentPayEntities: PayV2Entity[] = (() => {
    if (!compositionRes?.entities?.length || segmentEntityType === null) {
      return [];
    }
    const seen = new Set<number>();
    const out: PayV2Entity[] = [];
    for (const ent of compositionRes.entities) {
      const segments = ent.metrics?.[secondaryDimension] ?? [];
      for (const seg of segments) {
        if (seen.size >= MAX_SEGMENT_ENTITIES) break;
        const id = seg.metadata?.id;
        if (id !== undefined && !seen.has(id)) {
          seen.add(id);
          out.push({ EntID: id, EntType: segmentEntityType });
        }
      }
      if (seen.size >= MAX_SEGMENT_ENTITIES) break;
    }
    return out;
  })();

  const payFilterPrimaryDimension =
    secondaryDimension === 'geography' ? FilterId.GEOGRAPHY : FilterId.ROLE;
  const payFilters = getV2Filters({
    primaryDimension: payFilterPrimaryDimension,
    effectiveFilters,
    isSnapshot: false,
  });

  const payVariables: PayV2Req = {
    entities: segmentPayEntities,
    filters: payFilters,
    ...(geographyMode === 'administrative' && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };

  const pausePay =
    pauseComposition ||
    compositionStatus !== DataProviderQueryStatus.Success ||
    !compositionRes ||
    segmentPayEntities.length === 0 ||
    !payVariables.filters?.start_date ||
    !payVariables.filters?.end_date;

  const [{ data: payData, status: payStatus }] =
    useUrqlQueryWithStatus<GetPayV2Query>({
      query: PAY_V2_QUERY,
      variables: { req: payVariables },
      pause: pausePay,
    });

  const status = combineTwoQueryStatuses(compositionStatus, payStatus);

  return {
    payData: payData?.pay_v2 ?? undefined,
    compositionData: compositionRes ?? undefined,
    status,
  };
}
